import { memo, type ChangeEvent, useCallback, useMemo } from 'react';
import type {
  LoadHandlingPoiSpec as LoadHandlingPoiSpecType,
  LoadType,
  SingularStationArea,
} from '@thive/fddm-types';

import { QUARTER_ROTATION_RADIANS, THREE_QUARTER_ROTATION_RADIANS } from '@/helpers/angleConstants';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { MixedSwitch } from '@/modules/common/components/inputs';
import { getCommonBoolean } from '@/modules/common/helpers/commonValue';
import { useAppStore } from '@/modules/app/store/useAppStore';
import {
  getStationCarrierSide,
  getStationFootprintFromLoadType,
  getStationWidthForCarrierSide,
  type StationCarrierSide,
} from '@/modules/floorplanDesign/helpers/stationCapacity';
import { rotateConstellation } from '@/modules/floorplanDesign/helpers/rotateConstellation';
import { getStoredPoiAngle } from '@/modules/floorplanDesign/helpers/poiAngleHelpers';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';

interface Props {
  selectedSingularStationAreas: SingularStationArea[];
}

type StationWithPoiSpec = {
  area: SingularStationArea;
  poiSpec: LoadHandlingPoiSpecType;
};

/**
 * @description Returns the area with 'always fit station' switched. The station size and, for
 * long-side carriers, the POI and constellation angle follow the load type.
 */
const withAlwaysFitStation = (
  { area, poiSpec }: StationWithPoiSpec,
  loadType: LoadType | undefined,
  carrierSide: StationCarrierSide | undefined,
  alwaysFitStation: boolean,
): SingularStationArea => {
  let { stationSpec } = area;
  let nextPoiAngle = poiSpec.angle;
  let nextConstellationAngle = area.constellationSpec.angle;

  if (loadType && carrierSide) {
    if (alwaysFitStation) {
      stationSpec = {
        ...area.stationSpec,
        width: getStationWidthForCarrierSide(loadType, carrierSide),
      };

      if (carrierSide === 'long') {
        nextPoiAngle = 0;
        nextConstellationAngle = rotateConstellation(
          area.constellationSpec.angle,
          -QUARTER_ROTATION_RADIANS,
        );
      }
    } else {
      stationSpec = {
        ...area.stationSpec,
        ...getStationFootprintFromLoadType(loadType),
      };

      if (carrierSide === 'long') {
        nextPoiAngle = THREE_QUARTER_ROTATION_RADIANS;
        nextConstellationAngle = rotateConstellation(
          area.constellationSpec.angle,
          QUARTER_ROTATION_RADIANS,
        );
      }
    }
  }

  return {
    ...area,
    stationSpec,
    loadHandlingPoiSpecs: area.loadHandlingPoiSpecs.map((spec) => ({
      ...spec,
      angle: nextPoiAngle,
      alwaysFitStation,
    })),
    constellationSpec: {
      ...area.constellationSpec,
      angle: nextConstellationAngle,
    },
  };
};

/**
 * @description The 'always fit station' (deep stack) switch: sizes the station to the load carrier.
 * A load carrier setting, so it sits with the other load carrier settings.
 */
function AlwaysFitStationComponent({ selectedSingularStationAreas }: Props) {
  const mode = useAppStore((state) => state.mode);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const stations = useMemo(
    () =>
      selectedSingularStationAreas.flatMap((area): StationWithPoiSpec[] => {
        const [poiSpec] = area.loadHandlingPoiSpecs;
        return poiSpec ? [{ area, poiSpec }] : [];
      }),
    [selectedSingularStationAreas],
  );

  // null when the stations differ, shown as 'Mixed' by MixedSwitch
  const alwaysFitStation = useMemo(
    () => getCommonBoolean(stations.map(({ poiSpec }) => poiSpec.alwaysFitStation)),
    [stations],
  );

  const onFitChange = useCallback(
    (
      _event: ChangeEvent<HTMLInputElement>,
      newValue: LoadHandlingPoiSpecType['alwaysFitStation'],
    ) => {
      const updatedAreas = createEmptyAreas();
      let firstCarrierSide: StationCarrierSide | undefined;

      stations.forEach((station) => {
        if (station.poiSpec.alwaysFitStation === newValue) return;

        const loadType = fddmSvc.getLoadType(station.area.stationSpec.loadTypeIds[0]);
        const carrierSide = loadType ? getStationCarrierSide(station.area, loadType) : undefined;
        firstCarrierSide ??= carrierSide;

        pushAreaToItsCollection(
          updatedAreas,
          withAlwaysFitStation(station, loadType, carrierSide, newValue),
        );
      });

      if (firstCarrierSide) {
        fddmSvc.setPreference(
          'singularStationArea.loadHandlingPoiSpecs.angle',
          getStoredPoiAngle(firstCarrierSide),
        );
      }
      fddmSvc.setPreference('singularStationArea.loadHandlingPoiSpecs.alwaysFitStation', newValue);
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, stations],
  );

  // Hidden when any selected station has no load handling POI
  if (stations.length === 0 || stations.length !== selectedSingularStationAreas.length) {
    return null;
  }

  return (
    <PropertiesLabel i18nkey='interface:properties.area.always_fit_station'>
      <MixedSwitch
        disabled={mode !== 'EDITABLE'}
        checked={alwaysFitStation}
        onChange={onFitChange}
      />
    </PropertiesLabel>
  );
}

export const AlwaysFitStation = memo(AlwaysFitStationComponent);
